/**
 * types.ts — Contratos de tipos do Studio Core
 *
 * Núcleo neutro: não depende de DOM, React, Node nem Deno. É consumido tanto
 * pelo aplicativo quanto pelas Edge Functions, garantindo uma única fonte de
 * verdade para schema, blocos, contextos e capacidades.
 *
 * RELEASE: v1.1.0 - 2026-09-10 - Modos de renderização, declaração de capacidades,
 *           valor responsivo (ResponsiveValue) e propriedades compostas/avançadas
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 1)
 */

/** Versão atual do schema de documento do Studio. */
export const STUDIO_SCHEMA_VERSION = 1;

/** Forma de entrega do modelo. */
export type StudioOutput = 'web' | 'pdf' | 'email';

/** Natureza do documento (diferente da forma de entrega). */
export type StudioTemplateType =
  | 'proposal'
  /** Proposta em formato de site, entregue por link privado (não indexado). */
  | 'online_proposal'
  | 'landing_page'
  | 'email'
  | 'report'
  | 'quote';


/** Formato da fonte da versão. */
export type StudioSourceFormat = 'blocks' | 'raw_html';

/** Situação de modelo/versão. */
export type StudioStatus = 'draft' | 'published' | 'archived';

/** Famílias de bloco, com semânticas distintas. */
export type StudioBlockFamily = 'layout' | 'content' | 'data' | 'action';

/** Tipos suportados pelo catálogo de dados. */
export type StudioValueType =
  | 'string'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'percentage'
  | 'phone'
  | 'document'
  | 'boolean'
  | 'object'
  | 'collection';

// =============================================================================
// Modos de renderização e capacidades (contrato da Fase 0)
// =============================================================================

/**
 * Modo em que o documento está sendo desenhado.
 * Diferente de `StudioOutput`: a saída é a forma de entrega; o modo é o
 * contexto de desenho, que decide o quanto de efeito e movimento é permitido.
 */
export type StudioRenderMode = 'editor' | 'published' | 'thumbnail' | 'pdf' | 'email';

/** Todos os modos, na ordem em que aparecem na matriz de capacidades. */
export const STUDIO_RENDER_MODES: StudioRenderMode[] = [
  'editor',
  'published',
  'thumbnail',
  'pdf',
  'email',
];

/** Como um recurso se comporta em um modo específico. */
export type StudioModeSupport =
  /** Funciona integralmente. */
  | 'full'
  /** Desenhado sem movimento nem interação. */
  | 'static'
  /** Substituído por imagem de capa (vídeo). */
  | 'poster'
  /** Removido; a alternativa está em `fallback`. */
  | 'none';

/** Custo de desenho declarado por um recurso. */
export type StudioPerformanceCost = 'low' | 'medium' | 'high';

/**
 * Declaração obrigatória de qualquer recurso novo do catálogo
 * (bloco, efeito, movimento ou composição). Sem isto, o recurso não entra.
 */
export interface StudioCapabilityDeclaration {
  /** Versão do recurso, para evoluir sem quebrar documentos antigos. */
  version: number;
  /** Comportamento em cada modo de renderização. */
  supportedRenderModes: Record<StudioRenderMode, StudioModeSupport>;
  /** O que é desenhado quando o modo não suporta o recurso. */
  fallback: string;
  performanceCost: StudioPerformanceCost;
  /** O que acontece em tablet e celular. */
  responsiveBehavior: string;
  usesCanvas?: boolean;
  usesWebGL?: boolean;
  usesPointerTracking?: boolean;
  usesScroll?: boolean;
  usesMotion?: boolean;
}

// =============================================================================
// Valor responsivo
// =============================================================================

/** Tamanhos de tela atendidos pelo Studio. */
export type StudioBreakpoint = 'desktop' | 'tablet' | 'mobile';

/** Tamanhos de tela na ordem do maior para o menor. */
export const STUDIO_BREAKPOINTS: StudioBreakpoint[] = ['desktop', 'tablet', 'mobile'];

/**
 * Valor que pode variar por tamanho de tela.
 *
 * Estrutura genérica e única: span, ordem, visibilidade, posição, escala e
 * intensidade de efeito usam esta mesma forma, sem lógica duplicada.
 * Um valor simples (`T`) continua válido e significa "igual em todas as telas".
 */
export type StudioResponsiveValue<T> = T | { desktop?: T; tablet?: T; mobile?: T };

/** Verdadeiro quando o valor foi declarado por tamanho de tela. */
export function isResponsiveValue<T>(
  valor: StudioResponsiveValue<T> | undefined,
): valor is { desktop?: T; tablet?: T; mobile?: T } {
  if (!valor || typeof valor !== 'object' || Array.isArray(valor)) return false;
  return ['desktop', 'tablet', 'mobile'].some((chave) => chave in (valor as object));
}

/**
 * Resolve o valor para um tamanho de tela, herdando do tamanho maior quando
 * o menor não declarou nada (desktop → tablet → celular).
 */
export function resolveResponsive<T>(
  valor: StudioResponsiveValue<T> | undefined,
  tela: StudioBreakpoint,
): T | undefined {
  if (valor === undefined) return undefined;
  if (!isResponsiveValue(valor)) return valor;
  const cascata: StudioBreakpoint[] =
    tela === 'mobile' ? ['mobile', 'tablet', 'desktop']
    : tela === 'tablet' ? ['tablet', 'desktop']
    : ['desktop'];
  for (const chave of cascata) {
    const encontrado = valor[chave];
    if (encontrado !== undefined) return encontrado;
  }
  return undefined;
}

/** Tipo de campo editável no painel de propriedades. */
export type StudioPropType =
  | 'text'
  | 'richtext'
  | 'number'
  | 'boolean'
  | 'color'
  | 'select'
  | 'image'
  | 'spacing'
  /** Mesmo campo repetido por tamanho de tela (usa `of`). */
  | 'responsive'
  /** Conjunto de campos guardados em um único objeto (usa `fields`). */
  | 'group';

/** Definição de uma propriedade editável de bloco. */
export interface StudioBlockProp {
  key: string;
  label: string;
  type: StudioPropType;
  /** Valor padrão aplicado ao inserir o bloco. */
  defaultValue?: unknown;
  /** Opções para o tipo `select`. */
  options?: Array<{ value: string; label: string }>;
  /** Texto de ajuda exibido no painel de propriedades. */
  help?: string;
  /**
   * Só aparece no painel quando o modo avançado está ligado.
   * É o que mantém a tela simples para quem só quer montar a página.
   */
  advanced?: boolean;
  /** Campo interno repetido por tamanho de tela (obrigatório em `responsive`). */
  of?: Omit<StudioBlockProp, 'key' | 'of' | 'fields'> & { key?: string };
  /** Campos internos (obrigatório em `group`). */
  fields?: StudioBlockProp[];
  /**
   * Formato do texto quando a propriedade guarda vários itens em uma única
   * string (ex.: uma linha por item, campos separados por `|`).
   * Publicado no catálogo para que uma IA monte o conteúdo sem adivinhar.
   */
  syntax?: string;
  /** Exemplo pronto, no formato descrito em `syntax`. */
  example?: string;
}



/**
 * Definição de um bloco no catálogo.
 *
 * `provides` é o que permite validar publicação por CAPACIDADE e não por
 * presença de uma chave específica: um bloco novo que entregue as mesmas
 * capacidades satisfaz o requisito sozinho.
 */
export interface StudioBlockDefinition {
  key: string;
  label: string;
  family: StudioBlockFamily;
  icon?: string;
  /** Saídas em que o bloco sabe se renderizar. */
  outputs: StudioOutput[];
  /** Namespaces de dados que o bloco exige (ex.: 'proposal'). */
  requiresContexts?: string[];
  /** Capacidades entregues pelo bloco (ex.: 'proposal.items'). */
  provides?: string[];
  /** Pode conter outros blocos (seções e colunas). */
  container?: boolean;
  /** Contrato de capacidades (obrigatório para recursos novos — ver Fase 0). */
  capability?: StudioCapabilityDeclaration;
  props: StudioBlockProp[];
}

/** Nó de bloco dentro do documento. */
export interface StudioBlockNode {
  id: string;
  type: string;
  props: Record<string, unknown>;
  children?: StudioBlockNode[];
  /**
   * Espaço semântico que este bloco ocupa dentro de uma composição
   * (ex.: `title`, `visual`, `primaryAction`). É apenas informação: a
   * renderização NUNCA depende disto — serve para o editor orientar o usuário
   * e para a publicação avisar que um espaço obrigatório ficou vazio.
   */
  slotId?: string;
  /** Papel do espaço, na linguagem do catálogo de composições. */
  slotRole?: StudioSlotRole;
  /** Composição que originou este bloco (metadado de origem). */
  sourceComposition?: string;
  /** Versão da composição no momento em que foi inserida. */
  sourceCompositionVersion?: number;
}

/**
 * Papel de um espaço semântico dentro de uma composição.
 * Composições diferentes usam os MESMOS papéis, o que permite trocar de
 * composição sem perder o sentido do conteúdo.
 */
export type StudioSlotRole =
  /** Chapéu curto acima do título. */
  | 'eyebrow'
  /** Título principal. */
  | 'title'
  /** Texto de apoio. */
  | 'description'
  /** Ação principal (botão que leva ao objetivo da página). */
  | 'primaryAction'
  /** Ação secundária. */
  | 'secondaryAction'
  /** Peça visual: foto, tela do sistema, vídeo. */
  | 'visual'
  /** Prova social: avatares, nota, logotipos, depoimento. */
  | 'socialProof'
  /** Lista de itens: recursos, etapas, planos. */
  | 'list'
  /** Formulário de contato. */
  | 'form';

/** Tema do documento (tokens visuais). */
export interface StudioTheme {
  fontFamily?: string;
  fontSize?: string;
  textColor?: string;
  backgroundColor?: string;
  accentColor?: string;
  maxWidth?: string;
  /** Conjunto de fontes aprovado (`system`, `editorial`, `geometric`, `mono-accent`). */
  fontPreset?: string;
  /** Família própria dos títulos (sobrepõe a do conjunto). */
  headingFontFamily?: string;
  /** Peso dos títulos: 400 a 900. */
  headingWeight?: string;
  /** Caixa dos títulos: `none` ou `uppercase`. */
  headingTransform?: string;
  /** Espaçamento entre letras: `tight`, `normal`, `wide`, `wider`. */
  headingTracking?: string;
  [key: string]: unknown;
}

/**
 * Campos de identidade visual do documento, com exemplo real.
 * Publicado no catálogo para que uma IA saiba o que pode ajustar no `theme`.
 */
export const STUDIO_THEME_FIELDS = [
  { key: 'fontFamily', label: 'Família de fonte', example: "Inter, system-ui, sans-serif" },
  { key: 'fontSize', label: 'Tamanho do texto base', example: '16px' },
  { key: 'textColor', label: 'Cor do texto', example: '#1f2937' },
  { key: 'backgroundColor', label: 'Cor de fundo', example: '#ffffff' },
  { key: 'accentColor', label: 'Cor de destaque (botões e detalhes)', example: '#2563eb' },
  { key: 'maxWidth', label: 'Largura do miolo', example: '1120px' },
  { key: 'fontPreset', label: 'Conjunto de fontes aprovado', example: 'editorial' },
  { key: 'headingFontFamily', label: 'Família de fonte dos títulos', example: "Georgia, serif" },
  { key: 'headingWeight', label: 'Peso dos títulos', example: '800' },
  { key: 'headingTransform', label: 'Caixa dos títulos', example: 'uppercase' },
  { key: 'headingTracking', label: 'Espaçamento entre letras do título', example: 'tight' },
] as const;


/** Documento completo guardado em `source_content`. */
export interface StudioDocument {
  schemaVersion: number;
  blocks: StudioBlockNode[];
  theme?: StudioTheme;
  settings?: Record<string, unknown>;
}

/** Campo declarado por um contexto de dados. */
export interface StudioContextField {
  type: StudioValueType;
  label?: string;
  /** Para `collection`: campos de cada item. */
  itemSchema?: Record<string, StudioContextField>;
  /** Para `object`: campos internos. */
  fields?: Record<string, StudioContextField>;
  /** Exemplo real de retorno, usado no explorador de variáveis. */
  example?: string;
}

/** Definição de um namespace de dados. */
export interface StudioContextDefinition {
  namespace: string;
  label: string;
  schema: Record<string, StudioContextField>;
}

/** Definição de um tipo de modelo. */
export interface StudioTemplateDefinition {
  type: StudioTemplateType;
  label: string;
  /** Namespaces disponíveis para este tipo. */
  contexts: string[];
  /** Saídas permitidas. */
  outputs: StudioOutput[];
  /** Capacidades obrigatórias para publicar. */
  requiresCapabilities: string[];
  /** Aparece no formulário de criação de novo modelo. */
  creatable?: boolean;
  /** Texto curto de apoio exibido na criação. */
  hint?: string;
}


/** Resultado de validação. */
export interface StudioValidationIssue {
  code: string;
  message: string;
  blockId?: string;
}

export interface StudioValidationResult {
  valid: boolean;
  issues: StudioValidationIssue[];
}
